import { Autocomplete, debounce, FormControl, FormHelperText, FormLabel, TextField } from '@mui/material';
import { FC, FocusEventHandler, SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { SelectOptionDto } from '../../dtos';
import { emptyGuid } from '../../util';
import { QueryDefinition } from '@reduxjs/toolkit/dist/query';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';

export interface IGenericAutocompleteProps {
    selectedId: string;
    handleSelectedChange: (selectedJob?: SelectOptionDto) => void;
    required?: boolean;
    errorMessage?: string;
    onBlur?: FocusEventHandler<HTMLDivElement>;
    disabled?: boolean;
    query: UseQuery<QueryDefinition<any, any, any, SelectOptionDto[], any>>;
    queryParameters: any;
    label?: string;
}

export const GenericAutocomplete: FC<IGenericAutocompleteProps> = props => {
    const { selectedId, handleSelectedChange, required, errorMessage, onBlur, disabled, query, queryParameters, label } = props;
    const [inputValue, setInputValue] = useState('');
    const { data, isLoading, isFetching } = query({ searchString: inputValue, ...queryParameters });

    const debouncedInputChange = useMemo(
        () => debounce((value: string) => {
            setInputValue(value);
        }, 300)
        , [setInputValue]);

    const handleSelectChange = useCallback((_: SyntheticEvent<Element, Event>, value: SelectOptionDto | null) => {
        handleSelectedChange(value ?? undefined);
        setInputValue('');
    }, [handleSelectedChange]);

    const getSelectedOption = useCallback(() => {
        if (selectedId === '' || selectedId === emptyGuid || !data) {
            return null;
        }
        return data.find(x => x.id === selectedId) ?? null;
    }, [data, selectedId]);

    return (
        <FormControl error={!!errorMessage} fullWidth required={required} disabled={disabled}>
            {label && <FormLabel>{label}</FormLabel>}
            <Autocomplete
                onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                        debouncedInputChange(value)
                    }
                }}
                value={getSelectedOption()}
                onChange={handleSelectChange}
                loading={isLoading || isFetching}
                options={data ?? []}
                getOptionLabel={x => x.label}
                renderInput={(params) => (
                    <TextField
                        error={!!errorMessage}
                        {...params}
                    />
                )}
                onBlur={onBlur}
                disabled={disabled}
            />
            <FormHelperText>{errorMessage}</FormHelperText>
        </FormControl>
    )
}